// ============================================
// 🔐 PERMISSÕES - Controle de Acesso
// ============================================
// Define quais abas/funcionalidades cada perfil pode acessar
// ============================================

export const PERFIS = {
  ADMIN: 'admin',
  GERENTE: 'gerente',
  OPERADOR: 'operador',
  VENDEDOR: 'vendedor'
};

// MULTI-TENANT: roles do sistema multi-tenant
export const ROLES = {
  SUPER_ADMIN:  'super_admin',   // acesso a todos os tenants (painel admin.greentech.com)
  ADMIN_TENANT: 'admin_tenant',  // admin dentro do próprio tenant
  OPERADOR:     'operador',      // usuário normal dentro do tenant
};

/**
 * Retorna true se o usuário é super_admin (acesso cross-tenant).
 * @param {Object} usuario
 */
export const isSuperAdmin = (usuario) => usuario?.role === ROLES.SUPER_ADMIN;

// ============================================
// 📋 PERMISSÕES POR PERFIL
// ============================================
export const PERMISSOES = {
  // ========== ADMIN ==========
  // Acesso total ao sistema
  [PERFIS.ADMIN]: {
    abas: [
      'dashboard',
      'atendimento',
      'crm-recompra',
      'disparo',
      'financeiro',
      'vendas',
      'estoque',
      'cadastros',
      'usuarios',
      'instancias',
      'logs',
      'config-ia',
      'configuracoes',
      'relatorios',
      'whatsapp',
      'cadastro-produtos',
      'templates-meta',
      'metricas-meta',
      'entregas',
      'analises',
    ],
    subAbas: {
      disparo: ['grupo', 'personalizado', 'progresso'],
      financeiro: ['dashboard', 'vendas', 'despesas', 'estoque', 'dre']
    },
    acoes: {
      criarUsuario: true,
      editarUsuario: true,
      excluirUsuario: true,
      gerenciarPermissoes: true,
      verFinanceiro: true,
      editarFinanceiro: true,
      excluirFinanceiro: true,
      configurarIA: true,
      configurarWhatsApp: true,
      exportarRelatorios: true,
      importarDados: true,
      verTodosClientes: true,
      editarClientes: true,
      excluirClientes: true,
      enviarMensagens: true,
      verRelatorios: true
    }
  },

  // ========== GERENTE ==========
  // Acesso a operações e relatórios, mas não a configurações críticas
  [PERFIS.GERENTE]: {
    abas: [
      'dashboard',
      'atendimento',
      'crm-recompra',
      'disparo',
      'financeiro',
      'vendas',
      'estoque',
      'cadastros',
      'relatorios',
      'cadastro-produtos',
      'templates-meta',
      'metricas-meta',
      'entregas',
      'analises',
    ],
    subAbas: {
      disparo: ['grupo', 'personalizado', 'progresso'],
      financeiro: ['dashboard', 'vendas', 'despesas', 'estoque', 'dre']
    },
    acoes: {
      criarUsuario: false,
      editarUsuario: false,
      excluirUsuario: false,
      gerenciarPermissoes: false,
      verFinanceiro: true,
      editarFinanceiro: true,
      excluirFinanceiro: false,
      configurarIA: false,
      configurarWhatsApp: false,
      exportarRelatorios: true,
      importarDados: true,
      verTodosClientes: true,
      editarClientes: true,
      excluirClientes: false,
      enviarMensagens: true,
      verRelatorios: true
    }
  },

  // ========== VENDEDOR ==========
  // Acesso aos módulos da financeira + WhatsApp
  [PERFIS.VENDEDOR]: {
    abas: [
      'disparo',
      'cadastro-produtos',
      'whatsapp'
    ],
    subAbas: {
      disparo: ['grupo', 'personalizado', 'progresso'],
      financeiro: []
    },
    acoes: {
      criarUsuario: false,
      editarUsuario: false,
      excluirUsuario: false,
      gerenciarPermissoes: false,
      verFinanceiro: false,
      editarFinanceiro: false,
      excluirFinanceiro: false,
      configurarIA: false,
      configurarWhatsApp: false,
      exportarRelatorios: false,
      importarDados: false,
      verTodosClientes: false,
      editarClientes: false,
      excluirClientes: false,
      enviarMensagens: false,
      verRelatorios: false
    }
  },

  // ========== OPERADOR ==========
  // Acesso apenas a operações básicas
  [PERFIS.OPERADOR]: {
    abas: [
      'dashboard',
      'atendimento',
      'crm-recompra',
      'disparo',
      'cadastro-produtos'
    ],
    subAbas: {
      disparo: ['grupo', 'personalizado', 'progresso'],
      financeiro: [] // Sem acesso
    },
    acoes: {
      criarUsuario: false,
      editarUsuario: false,
      excluirUsuario: false,
      gerenciarPermissoes: false,
      verFinanceiro: false,
      editarFinanceiro: false,
      excluirFinanceiro: false,
      configurarIA: false,
      configurarWhatsApp: false,
      exportarRelatorios: false,
      importarDados: false,
      verTodosClientes: true,
      editarClientes: true,
      excluirClientes: false,
      enviarMensagens: true,
      verRelatorios: false
    }
  }
};

// ============================================
// 🔍 FUNÇÕES AUXILIARES
// ============================================

/**
 * Verifica se o usuário tem acesso a uma aba
 */
export const podeAcessarAba = (usuario, abaId) => {
  if (!usuario || !usuario.perfil) return false;
  const permissoes = PERMISSOES[usuario.perfil];
  return permissoes && permissoes.abas.includes(abaId);
};

/**
 * Verifica se o usuário tem acesso a uma sub-aba
 */
export const podeAcessarSubAba = (usuario, abaId, subAbaId) => {
  if (!usuario || !usuario.perfil) return false;
  const permissoes = PERMISSOES[usuario.perfil];
  return permissoes && 
         permissoes.subAbas[abaId] && 
         permissoes.subAbas[abaId].includes(subAbaId);
};

/**
 * Verifica se o usuário pode executar uma ação
 */
export const podeExecutarAcao = (usuario, acao) => {
  if (!usuario || !usuario.perfil) return false;
  const permissoes = PERMISSOES[usuario.perfil];
  return permissoes && permissoes.acoes[acao] === true;
};

/**
 * Retorna todas as abas acessíveis para o usuário
 */
export const getAbasAcessiveis = (usuario) => {
  if (!usuario || !usuario.perfil) return [];
  const permissoes = PERMISSOES[usuario.perfil];
  return permissoes ? permissoes.abas : [];
};

/**
 * Retorna o nome legível do perfil
 */
export const getNomePerfil = (perfil) => {
  const nomes = {
    [PERFIS.ADMIN]: 'Administrador',
    [PERFIS.GERENTE]: 'Gerente',
    [PERFIS.OPERADOR]: 'Operador',
    [PERFIS.VENDEDOR]: 'Vendedor'
  };
  return nomes[perfil] || perfil;
};

/**
 * Retorna a cor do perfil para badges
 */
export const getCorPerfil = (perfil) => {
  const cores = {
    [PERFIS.ADMIN]: 'bg-red-500/20 text-red-400 border-red-500/30',
    [PERFIS.GERENTE]: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    [PERFIS.OPERADOR]: 'bg-green-500/20 text-green-400 border-green-500/30',
    [PERFIS.VENDEDOR]: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
  };
  return cores[perfil] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
};
